import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {ImageOpPerPixelFn} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/node"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {
    createImageOpImgProc,
    getImgProcChannelLayoutByCount,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/combine-channels/definition"
import {assertSameSizeAndBatchSize} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {HalCompositorInfo} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/hal"
import {ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"

@registerNode
export class ImageOpNodeCombineChannels extends ImageOpPerPixelFn<typeof definition> {
    static nodeClass = "ImageOpNodeCombineChannels"
    static definition = definition

    constructor(parameters: ImageOpInputsOrNodes<typeof definition>) {
        super(parameters)
        this.listenToParameterChange("sourceImageA").subscribe(() => this.requestCompositorRebuild())
    }

    protected getCompositingInfo(): HalCompositorInfo {
        const numChannels = this.parameters.sourceImageA ? 4 : 3
        return {
            compositingFunction: `
                float r = fetchSourceChannel(0, 0);
                float g = ${numChannels >= 2 ? "fetchSourceChannel(1, 0)" : "0.0"};
                float b = ${numChannels >= 3 ? "fetchSourceChannel(2, 0)" : "0.0"};
                float a = ${numChannels >= 4 ? "fetchSourceChannel(3, 0)" : "0.0"};
                return vec4(r, g, b, a);
                `,
            sourceParameterNames:
                numChannels === 4 ? ["sourceImageR", "sourceImageG", "sourceImageB", "sourceImageA"] : ["sourceImageR", "sourceImageG", "sourceImageB"],
            outDescriptor: {
                channels: numChannels,
            },
        }
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof definition, "imgproc">) => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        const sourceImages = [evaluatedParameters.sourceImageR, evaluatedParameters.sourceImageG, evaluatedParameters.sourceImageB]
        if (evaluatedParameters.sourceImageA) {
            sourceImages.push(evaluatedParameters.sourceImageA)
        }
        const primarySourceImage = sourceImages[0]
        for (const sourceImage of sourceImages) {
            assertSameSizeAndBatchSize(primarySourceImage.descriptor, sourceImage.descriptor)
        }
        const imgProcChannelLayout = getImgProcChannelLayoutByCount(sourceImages.length)
        if (imgProcChannelLayout === "L") {
            throw Error("ImgProc's CombineChannels does not support single channel images")
        }
        const descriptor = this.determineDescriptor(evaluatedParameters)
        return createImageOpImgProc(descriptor, {
            type: "combineChannels",
            input: sourceImages.map((sourceImage) => sourceImage.node),
            channelLayout: imgProcChannelLayout,
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)
    }
}
